import {
  Button,
  DialogActions,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { isNullish } from 'remeda';
import { CustomChip } from 'src/components/AddComplimentaryPeriodModal/custom-chip';
import { type Address, useOfferedProducts } from 'src/graphql';
import {
  AddPreSoldMembershipFormFields,
  convertPriceToDollars,
  getLengthInMonthsFromInterval,
  MAX_PRICE,
  preSoldLengths,
} from './util';

export interface AddPreSoldMembershipFormValues {
  price: number;
  lengthInMonths: number;
  partnerProductId: string;
}

interface AddPreSoldMembershipFormProps {
  address?: Address | void | null;
  onSubmit?: (data: AddPreSoldMembershipFormValues) => void;
  formMethods: UseFormReturn<AddPreSoldMembershipFormValues>;
}

export const AddPreSoldMembershipForm = ({
  formMethods,
  onSubmit,
  address,
}: AddPreSoldMembershipFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = formMethods;

  const theme = useTheme();

  const { offeredProducts, loading: isLoadingOfferedProducts } = useOfferedProducts({
    brandId: address?.brandId,
    partnerId: address?.partnerId,
  });

  const sortedOfferedProducts = useMemo(
    () => offeredProducts?.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [offeredProducts],
  );

  const [selectedChip, setSelectedChip] = useState(preSoldLengths[0]);

  const unscheduledPreSoldMembership = useMemo(() => {
    const psm = address?.preSoldMemberships?.[0];
    if (!psm || psm.isScheduled) {
      return null;
    }

    return psm;
  }, [address?.preSoldMemberships]);

  useEffect(() => {
    if (
      unscheduledPreSoldMembership?.id ||
      unscheduledPreSoldMembership?.preSoldMembershipId
    ) {
      if (unscheduledPreSoldMembership.partnerProductId) {
        setValue(
          AddPreSoldMembershipFormFields.PartnerProductId,
          unscheduledPreSoldMembership.partnerProductId,
        );
      }

      if (unscheduledPreSoldMembership.amount) {
        setValue(
          AddPreSoldMembershipFormFields.Price,
          convertPriceToDollars(unscheduledPreSoldMembership.amount),
        );
      }

      const lengthInMonths =
        unscheduledPreSoldMembership.intervalCount && unscheduledPreSoldMembership.intervalUnit
          ? getLengthInMonthsFromInterval(
              unscheduledPreSoldMembership.intervalCount,
              unscheduledPreSoldMembership.intervalUnit,
            )
          : preSoldLengths[0].value;

      setValue(AddPreSoldMembershipFormFields.LengthInMonths, lengthInMonths);
    }

    return () => {
      setSelectedChip(preSoldLengths[0]);
      formMethods.reset();
    };
  }, [unscheduledPreSoldMembership, setValue, formMethods]);

  useEffect(() => {
    if (sortedOfferedProducts?.length && !unscheduledPreSoldMembership) {
      setValue(
        AddPreSoldMembershipFormFields.PartnerProductId,
        sortedOfferedProducts[0].partnerProductId,
      );
    }
  }, [sortedOfferedProducts, setValue, unscheduledPreSoldMembership]);

  const watchedLength = formMethods.watch(AddPreSoldMembershipFormFields.LengthInMonths);
  useEffect(() => {
    setSelectedChip(
      preSoldLengths.find((el) => el.value === watchedLength) || preSoldLengths[0],
    );
  }, [watchedLength]);

  const onChipChange = useCallback(
    (item: (typeof preSoldLengths)[number]) => {
      setSelectedChip(item);
      setValue(AddPreSoldMembershipFormFields.LengthInMonths, item.value);
    },
    [setValue],
  );

  return (
    <form onSubmit={onSubmit ? handleSubmit(onSubmit) : undefined}>
      <Controller
        name={AddPreSoldMembershipFormFields.PartnerProductId}
        control={control}
        rules={{
          validate: (v) =>
            (typeof v === 'string' && v.trim().length > 0) || 'Select a service level',
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth margin="normal" error={!!error}>
            <InputLabel id="serviceLevel-label">Service Level</InputLabel>
            <Select
              {...field}
              size="small"
              labelId="serviceLevel-label"
              label="Service Level"
              disabled={isLoadingOfferedProducts}
              value={field.value || ''}
            >
              {sortedOfferedProducts?.map((product) => (
                <MenuItem key={product.partnerProductId} value={product.partnerProductId}>
                  {formatMembershipName(product.displayName)}
                </MenuItem>
              ))}
            </Select>
            {error ? <FormHelperText>{error.message}</FormHelperText> : null}
          </FormControl>
        )}
      />

      <Typography mt={1} variant="subtitle2">
        Length
      </Typography>
      <div>
        <Stack direction="row" justifyContent="flex-start" flexWrap="wrap" mb={1}>
          {preSoldLengths.map((el) => (
            <CustomChip
              key={el.label}
              label={el.label}
              checked={el.label === selectedChip.label}
              onClick={() => onChipChange(el)}
            />
          ))}
        </Stack>
      </div>

      <Controller
        name={AddPreSoldMembershipFormFields.Price}
        control={control}
        rules={{
          validate: (value) => {
            if (isNullish(value) || Number.isNaN(value)) {
              return 'Enter the amount the client paid';
            }
            if (typeof value === 'number' && value <= 0) {
              return 'Enter a price greater than 0';
            }
            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <NumericFormat
            fullWidth
            size="small"
            sx={{ marginTop: theme.spacing(2) }}
            label="How much did the client pay?"
            placeholder="0.00"
            type="text"
            valueIsNumericString
            customInput={TextField}
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            isAllowed={({ floatValue = 0 }) => floatValue >= 0 && floatValue < MAX_PRICE}
            value={field.value || 0}
            thousandSeparator
            onValueChange={(values) => {
              setValue(AddPreSoldMembershipFormFields.Price, values.floatValue || 0, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      {onSubmit ? (
        <DialogActions sx={{ marginTop: '1rem' }}>
          <Button disabled={isSubmitting} variant="text" color="primary" type="submit">
            Submit
          </Button>
        </DialogActions>
      ) : null}
    </form>
  );
};
